import { addDays, differenceInCalendarDays, format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import i18n from "@/lib/i18n";
import {
  apiToZoned,
  extractZonedDateStr,
  extractZonedTimeStr,
  formatZoned,
  nowAsZonedDate,
} from "@/lib/datetime/companyTimezone";
import { getDateLocale, uses12HourClock } from "@/lib/timeFormatting";
import { canonicalToDisplay } from "../components/ui/TimeTextInput";

/**
 * The default delivery date for a brand-new order: always the next calendar
 * day relative to today in the company timezone. Deliberately blind — it never
 * reads persisted/selected state, so opening the new-order picker always
 * prefills tomorrow regardless of what was picked before.
 */
export function nextDeliveryDate(tz: string): Date {
  return addDays(nowAsZonedDate(tz), 1);
}

/**
 * Whole-day offset of a delivery date from an anchor date (the order date,
 * falling back to `created_at`). Both sides are resolved to the company-tz
 * calendar day first — `delivery_date` is a date-only column (resolved to
 * itself) while the timestamp anchor is tz-converted to match the day the UI
 * shows. `+1` is the day after the order (the expected next-day delivery), `0`
 * is same-day, negative means the delivery falls before the order date.
 */
export function deliveryDayOffset(
  deliveryDate: string,
  anchorDate: string,
  tz: string,
): number {
  const deliveryYmd = extractZonedDateStr(deliveryDate, tz) ?? deliveryDate;
  const anchorYmd = extractZonedDateStr(anchorDate, tz) ?? anchorDate;
  return differenceInCalendarDays(
    parseDateOnly(deliveryYmd),
    parseDateOnly(anchorYmd),
  );
}

export function formatDateOnly(date: Date | string): string {
  try {
    const d = typeof date === "string" ? new Date(date) : date;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return new Date().toISOString().split("T")[0];
  }
}

export function parseDateOnly(dateStr: string): Date {
  try {
    const dateOnly = dateStr.split("T")[0];
    const [year, month, day] = dateOnly.split("-").map(Number);
    if (!year || !month || !day || isNaN(year) || isNaN(month) || isNaN(day)) {
      return new Date();
    }
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch {
    return new Date();
  }
}

export function formatDateLocale(
  date: Date,
  locale?: string,
  includeYear = true,
): string {
  try {
    const lang = locale || i18n.language || "fr";
    const dateFnsLocale = lang === "fr" ? fr : enUS;
    const pattern =
      lang === "fr"
        ? includeYear
          ? "d MMM yyyy"
          : "d MMM"
        : includeYear
          ? "MMM d, yyyy"
          : "MMM d";
    return format(date, pattern, { locale: dateFnsLocale });
  } catch {
    return date.toLocaleDateString();
  }
}

/**
 * Order-date display for the recap list view: the company-tz calendar date
 * (no year, matching the column) plus the wall-clock time when one is
 * present. Date-only or midnight values render as just the date — that is
 * the "show the time when available" rule. `dateStr` is an absolute UTC+0
 * API datetime; `tz` is the company IANA zone.
 */
export function formatOrderDateWithTime(
  dateStr: string | null | undefined,
  tz: string,
): string {
  if (!dateStr) return "—";
  const date = apiToZoned(dateStr, tz);
  if (!date) return "—";
  const datePart = formatDateLocale(date, undefined, false);
  const timePart = extractZonedTimeStr(dateStr, tz);
  if (timePart === "00:00") return datePart;
  // Time is locale-clocked for display (en → "2:30 PM", fr/es → "14:30");
  // the canonical 24h value stays in the API/storage layer.
  const displayTime = canonicalToDisplay(
    timePart,
    uses12HourClock(i18n.language),
  );
  return `${datePart} · ${displayTime}`;
}

/**
 * Full, readable order-date display for the focus DATES box: FULL weekday +
 * day + FULL month (e.g. "mercredi 16 juillet" / "Wednesday 16 July") plus the
 * wall-clock time when present. Same time rules as `formatOrderDateWithTime`,
 * but spelled out — the recap list keeps the compact `formatOrderDateWithTime`.
 */
export function formatOrderDateTimeFull(
  dateStr: string | null | undefined,
  tz: string,
): string {
  if (!dateStr) return "—";
  const lang = i18n.language;
  const datePart = formatZoned(dateStr, tz, "EEEE d MMMM", {
    locale: getDateLocale(lang),
  });
  if (!datePart) return "—";
  const timePart = extractZonedTimeStr(dateStr, tz);
  if (timePart === "00:00") return datePart;
  const displayTime = canonicalToDisplay(timePart, uses12HourClock(lang));
  return `${datePart} · ${displayTime}`;
}

export function getDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(start);
  while (current <= end) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export function isToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}
